
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Title,
  Text,
  Card,
  Group,
  Button,
  Stack,
  Alert,
  Progress,
  Badge,
  Box,
  ActionIcon,
  Tooltip,
  ThemeIcon,
  Modal,
  Transition,
  Divider,
  CloseButton
} from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { notifications } from '@mantine/notifications';
import {
  IconUpload,
  IconFile,
  IconX,
  IconCheck,
  IconAlertCircle,
  IconRefresh,
  IconTrash,
  IconChevronRight,
  IconArrowLeft
} from '@tabler/icons-react';
import { useAuth0 } from '@auth0/auth0-react';
import { authFetch } from './utils/authFetch';

const CATEGORIES = [
  { 
    value: 'risk-catalogues', 
    label: 'Risk Catalogues', 
    description: 'NIST, ISO, industry frameworks',
    icon: '📊',
    color: 'blue'
  },
  { 
    value: 'policy-governance', 
    label: 'Governance Policies', 
    description: 'Corporate governance, board policies',
    icon: '🏛️',
    color: 'violet'
  },
  { 
    value: 'policy-security', 
    label: 'Security Policies', 
    description: 'Information security, data protection',
    icon: '🔒',
    color: 'red'
  },
  { 
    value: 'policy-ai-control', 
    label: 'AI Control Policies', 
    description: 'AI governance, model management',
    icon: '🤖',
    color: 'green'
  }
];

const SUPPORTED_FILE_TYPES = [
  'PDF', 'DOCX', 'DOC', 'XLSX', 'XLS', 'CSV', 'TXT', 'MD', 'HTML', 'RTF', 'PPTX'
];

const FILE_SIZE_LIMIT = 50 * 1024 * 1024; // 50MB

function UniversalFileUploader({ categoryFilter }) {
  const { getAccessTokenSilently } = useAuth0();
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [embeddingJobs, setEmbeddingJobs] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [globalError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteModal, setDeleteModal] = useState({ open: false, file: null });

  // Restrict the visible categories when the host page only owns a slice
  // of the corpus (e.g. /agency/policies only shows the 3 policy types;
  // Risk Knowledge Base · Catalogues tab only shows risk-catalogues).
  // Without categoryFilter the component renders all 4 categories
  // (legacy behaviour for any caller that still uses it generically).
  const visibleCategories = useMemo(() => {
    if (!Array.isArray(categoryFilter) || categoryFilter.length === 0) return CATEGORIES;
    const allowed = new Set(categoryFilter);
    return CATEGORIES.filter((c) => allowed.has(c.value));
  }, [categoryFilter]);

  // Heading + subhead reflect which slice the page is rendering. Each
  // surface (policies route, catalogues tab, full uploader) gets its
  // own copy without callers having to pass strings.
  const { headerTitle, headerSubtitle } = useMemo(() => {
    const visibleValues = new Set(visibleCategories.map((c) => c.value));
    const onlyCatalogues = visibleValues.size === 1 && visibleValues.has('risk-catalogues');
    const onlyPolicies =
      visibleValues.size > 0 &&
      [...visibleValues].every((v) => v.startsWith('policy-'));
    if (onlyCatalogues) {
      return {
        headerTitle: 'Risk catalogues and registers',
        headerSubtitle: 'Upload NIST / ISO frameworks, taxonomies, and risk registers for retrieval at runtime.',
      };
    }
    if (onlyPolicies) {
      return {
        headerTitle: 'Policies',
        headerSubtitle: 'Upload governance, security, and AI-control policies for retrieval at runtime.',
      };
    }
    return {
      headerTitle: 'Risk Catalogues and Policies',
      headerSubtitle: 'Upload documents for AI-powered risk analysis and policy management. Select a category to get started.',
    };
  }, [visibleCategories]);

  // Fetch embedding jobs from Supabase for UI display (observability only)
  const fetchEmbeddingJobs = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_AUTH0_AUDIENCE || 'auth.agenthuginn.ai',
          scope: 'read:models write:models'
        }
      });

      const response = await authFetch('/.netlify/functions/getEmbeddingJobs', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const jobs = await response.json();
        setEmbeddingJobs(jobs);
      }
    } catch (error) {
      console.error('Failed to fetch embedding jobs:', error);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  // Load data on mount
  useEffect(() => {
    fetchEmbeddingJobs();
  }, [fetchEmbeddingJobs]);

  // File status: 'uploading', 'uploaded', 'processing', 'completed', 'failed'
  const uploadFileToSupabase = useCallback(async (fileItem) => {
    try {
      console.log(`[UniversalFileUploader] Starting upload for file: ${fileItem.name}`);
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_AUTH0_AUDIENCE || 'auth.agenthuginn.ai',
          scope: 'read:models write:models'
        }
      });

      const bucket = getDynamicBucket(fileItem.category);
      const path = `${fileItem.category}/${fileItem.name}`;
      const contentType = fileItem.file.type || 'application/octet-stream';
      
      console.log(`[UniversalFileUploader] Uploading to bucket: ${bucket}, path: ${path}`);

      const uploadResponse = await authFetch('/.netlify/functions/supaCreateSignedUpload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          bucket,
          path,
          contentType
        })
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed: ${uploadResponse.statusText}`);
      }

      const uploadData = await uploadResponse.json();

      // Upload file to signed URL  
      const fileUploadResponse = await fetch(uploadData.signedURL || uploadData.signedUrl, {
        method: 'PUT',
        body: fileItem.file,
        headers: {
          'Content-Type': contentType
        }
      });

      if (!fileUploadResponse.ok) {
        throw new Error('Failed to upload file to storage');
      }

      console.log(`[UniversalFileUploader] File uploaded successfully: ${fileItem.name}`);
      
      // Update file status to uploaded
      setUploadedFiles(prev => prev.map(item => 
        item.id === fileItem.id ? { 
          ...item, 
          status: 'uploaded', 
          progress: 100,
          supabasePath: uploadData.path || path
        } : item
      ));

      notifications.show({
        title: 'File Uploaded',
        message: `${fileItem.name} uploaded successfully. Click "Process" to embed.`,
        color: 'green',
        icon: <IconCheck size={16} />
      });

    } catch (error) {
      console.error('File upload error:', error);
      
      setUploadedFiles(prev => prev.map(item => 
        item.id === fileItem.id ? { 
          ...item, 
          status: 'failed', 
          progress: 0,
          error: error.message 
        } : item
      ));

      notifications.show({
        title: 'Upload Failed',
        message: `${fileItem.name}: ${error.message}`,
        color: 'red',
        icon: <IconX size={16} />
      });
    }
  }, [getAccessTokenSilently]);

  const addFilesToList = useCallback((files, category) => {
    files.forEach(file => {
      // Check for duplicates in both session files and persistent jobs
      const existingInSession = uploadedFiles.some(item => 
        item.name === file.name && item.category === category
      );
      const existingInJobs = embeddingJobs.some(job => 
        job.file_name === file.name && job.category === category && job.status === 'completed'
      );
      
      if (existingInSession || existingInJobs) {
        notifications.show({
          title: 'Duplicate File',
          message: `${file.name} has already been uploaded${existingInJobs ? ' and processed' : ''}`,
          color: 'orange',
          icon: <IconAlertCircle size={16} />
        });
        return;
      }

      const fileItem = {
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        file,
        name: file.name,
        size: file.size,
        type: file.name.split('.').pop().toUpperCase(),
        status: 'uploading',
        progress: 0,
        error: null,
        supabasePath: null,
        category
      };
      
      setUploadedFiles(prev => [...prev, fileItem]);
      uploadFileToSupabase(fileItem);
    });
  }, [uploadFileToSupabase, uploadedFiles, embeddingJobs]);

  const getDynamicBucket = (category) => {
    if (category === 'risk-catalogues') return 'risk-catalogues';
    if (category.startsWith('policy-')) return 'policies';
    return 'documents'; // fallback
  };

  const processFile = async (fileItem) => {
    try {
      console.log(`[UniversalFileUploader] Processing file: ${fileItem.name}`);
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_AUTH0_AUDIENCE || 'auth.agenthuginn.ai',
          scope: 'read:models write:models'
        }
      });

      // Update status to processing
      setUploadedFiles(prev => prev.map(item => 
        item.id === fileItem.id ? { ...item, status: 'processing', progress: 25 } : item
      ));

      // Use EXACT same approach as model cards: supaLlamaStart + supaLlamaFinalize
      const bucket = getDynamicBucket(fileItem.category);
      const path = fileItem.supabasePath;

      // Step 1: Start LlamaParse (returns jobId immediately like model cards)
      const startResponse = await authFetch('/.netlify/functions/supaLlamaStart', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          bucket,
          path,
          fileName: fileItem.name
        })
      });

      if (!startResponse.ok) {
        const errorData = await startResponse.json().catch(() => ({}));
        throw new Error(errorData.error || `Start failed: ${startResponse.statusText}`);
      }

      const startData = await startResponse.json();
      const jobId = startData.jobId;
      console.log(`[UniversalFileUploader] LlamaParse started, jobId: ${jobId}`);

      // Update status to processing
      setUploadedFiles(prev => prev.map(item => 
        item.id === fileItem.id ? { 
          ...item, 
          status: 'processing', 
          progress: 25,
          jobId
        } : item
      ));

      // Step 2: Poll with supaLlamaFinalize (exact model card approach)
      pollLlamaJob(fileItem, jobId, bucket, path);

    } catch (error) {
      console.error('File processing error:', error);
      
      setUploadedFiles(prev => prev.map(item => 
        item.id === fileItem.id ? { 
          ...item, 
          status: 'failed', 
          progress: 0,
          error: error.message 
        } : item
      ));

      notifications.show({
        title: 'Processing Failed',
        message: `${fileItem.name}: ${error.message}`,
        color: 'red',
        icon: <IconX size={16} />
      });
    }
  };

  const pollLlamaJob = async (fileItem, jobId, bucket, path) => {
    // EXACT same polling logic as model cards in AutopilotAssessmentFlow.jsx
    let attempts = 0;
    const maxAttempts = 30;

    const poll = async () => {
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_AUTH0_AUDIENCE || 'auth.agenthuginn.ai',
            scope: 'read:models write:models'
          }
        });

        const finResponse = await authFetch('/.netlify/functions/supaLlamaFinalize', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            jobId,
            bucket,
            path,
            fileName: fileItem.name,
            category: fileItem.category, // Add category for proper table routing
            saveMarkdown: true
          })
        });

        // Handle 202 status (still processing) - this is normal!
        if (finResponse.status === 202) {
          // Still processing, continue polling (same as model cards)
          attempts++;
          if (attempts < maxAttempts) {
            const statusData = await finResponse.json().catch(() => ({}));
            console.log(`[UniversalFileUploader] Polling attempt ${attempts}/${maxAttempts} for ${fileItem.name}, status: ${statusData.status}`);
            setUploadedFiles(prev => prev.map(item => 
              item.id === fileItem.id ? { 
                ...item, 
                progress: Math.min(25 + (attempts * 2), 90) // Gradual progress
              } : item
            ));
            setTimeout(poll, 3000); // Same 3s interval as model cards
            return;
          } else {
            throw new Error('Polling timeout after 30 attempts');
          }
        }

        // Handle other error statuses  
        if (!finResponse.ok) {
          const errorText = await finResponse.text();
          throw new Error(`Finalize failed: ${errorText}`);
        }

        // Success! Parse response
        const finData = await finResponse.json();
        console.log(`[UniversalFileUploader] Processing completed for ${fileItem.name}:`, finData);

        // Update to completed
        setUploadedFiles(prev => prev.map(item => 
          item.id === fileItem.id ? { 
            ...item, 
            status: 'completed',
            progress: 100,
            chunksProcessed: finData.embeddedChunks,
            totalChunks: finData.totalChunks
          } : item
        ));

        notifications.show({
          title: 'Processing Complete',
          message: `${fileItem.name} processed successfully (${finData.embeddedChunks}/${finData.totalChunks} chunks)`,
          color: 'green',
          icon: <IconCheck size={16} />
        });

      } catch (error) {
        console.error(`Polling error for ${fileItem.name}:`, error);
        
        setUploadedFiles(prev => prev.map(item => 
          item.id === fileItem.id ? { 
            ...item, 
            status: 'failed',
            error: error.message
          } : item
        ));

        notifications.show({
          title: 'Processing Failed',
          message: `${fileItem.name}: ${error.message}`,
          color: 'red',
          icon: <IconX size={16} />
        });
      }
    };

    // Start polling immediately
    poll();
  };

  const removeFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(item => item.id !== fileId));
  };

  const retryFile = async (fileId) => {
    const fileItem = uploadedFiles.find(item => item.id === fileId);
    if (fileItem) {
      // Reset status and retry upload
      setUploadedFiles(prev => prev.map(item => 
        item.id === fileId ? { ...item, status: 'uploading', progress: 0, error: null } : item
      ));
      
      // Retry the actual upload
      uploadFileToSupabase(fileItem);
    }
  };

  const clearCompleted = (category = null) => {
    if (category) {
      setUploadedFiles(prev => prev.filter(item => 
        !(item.category === category && item.status === 'completed')
      ));
    } else {
      setUploadedFiles(prev => prev.filter(item => item.status !== 'completed'));
    }
  };

  const handleDeleteFile = useCallback(async (fileToDelete) => {
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_AUTH0_AUDIENCE || 'auth.agenthuginn.ai',
          scope: 'read:models write:models'
        }
      });

      const response = await authFetch('/.netlify/functions/deleteFile', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fileName: fileToDelete.name,
          category: fileToDelete.category,
          jobId: fileToDelete.isPersistent ? fileToDelete.id : null,
          deleteEmbeddings: fileToDelete.status === 'completed'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to delete file');
      }

      // Remove from local state if it's a session file
      if (!fileToDelete.isPersistent) {
        setUploadedFiles(prev => prev.filter(item => item.id !== fileToDelete.id));
      }

      // Refresh embedding jobs to update persistent files
      await fetchEmbeddingJobs();

      notifications.show({
        title: 'File Deleted',
        message: `${fileToDelete.name} has been deleted successfully`,
        color: 'green',
        icon: <IconCheck size={16} />
      });

      setDeleteModal({ open: false, file: null });

    } catch (error) {
      console.error('Delete file error:', error);
      notifications.show({
        title: 'Delete Failed',
        message: error.message,
        color: 'red',
        icon: <IconX size={16} />
      });
    }
  }, [getAccessTokenSilently, fetchEmbeddingJobs]);

  const getCategoryStats = (category) => {
    // Combine current session files with persistent embedding jobs
    const categoryFiles = uploadedFiles.filter(item => item.category === category);
    const categoryJobs = embeddingJobs.filter(job => job.category === category);
    
    // Stats from current session
    const sessionCompleted = categoryFiles.filter(item => item.status === 'completed').length;
    const sessionProcessing = categoryFiles.filter(item => item.status === 'processing').length;
    const sessionFailed = categoryFiles.filter(item => item.status === 'failed').length;
    const sessionChunks = categoryFiles.reduce((sum, item) => sum + (item.chunksProcessed || 0), 0);
    
    // Stats from persistent jobs
    const jobsCompleted = categoryJobs.filter(job => job.status === 'completed').length;
    const jobsProcessing = categoryJobs.filter(job => job.status === 'processing').length;
    const jobsFailed = categoryJobs.filter(job => job.status === 'failed').length;
    const jobsChunks = categoryJobs.reduce((sum, job) => sum + (job.chunks_processed || 0), 0);
    
    return {
      totalFiles: categoryFiles.length + categoryJobs.length,
      completed: sessionCompleted + jobsCompleted,
      processing: sessionProcessing + jobsProcessing,
      failed: sessionFailed + jobsFailed,
      totalChunks: sessionChunks + jobsChunks
    };
  };

  const getStatusColor = (status) => {
    const colors = {
      uploading: 'blue',
      uploaded: 'cyan',
      processing: 'yellow',
      completed: 'green',
      failed: 'red'
    };
    return colors[status] || 'gray';
  };

  const getStatusIcon = (status) => {
    const icons = {
      uploading: <IconUpload size={16} />,
      uploaded: <IconCheck size={16} />,
      processing: <IconRefresh size={16} />,
      completed: <IconCheck size={16} />,
      failed: <IconX size={16} />
    };
    return icons[status] || <IconFile size={16} />;
  };

  const getCategoryFiles = (category) => {
    const categoryFiles = uploadedFiles.filter(item => item.category === category);
    const categoryJobs = embeddingJobs.filter(job => job.category === category);
    
    // Combine session files and persistent jobs for display
    const allCategoryItems = [
      ...categoryFiles,
      ...categoryJobs.map(job => ({
        id: job.job_id,
        name: job.file_name,
        type: job.file_type,
        size: job.file_size || 0,
        status: job.status,
        chunksProcessed: job.chunks_processed || 0,
        totalChunks: job.total_chunks || 0,
        error: job.error_message,
        category: job.category,
        isPersistent: true,
        created_at: job.created_at,
        processing_time_ms: job.processing_time_ms
      }))
    ];
    
    // Deduplicate by filename, keeping the most recent/complete entry
    const deduplicatedItems = [];
    const seenFiles = new Map();
    
    // Sort by creation time (newest first) before deduplication
    allCategoryItems.sort((a, b) => {
      const aTime = a.created_at || a.uploadedAt || 0;
      const bTime = b.created_at || b.uploadedAt || 0;
      return new Date(bTime) - new Date(aTime);
    });
    
    // Deduplicate: keep first occurrence (newest) of each filename
    for (const item of allCategoryItems) {
      if (!seenFiles.has(item.name)) {
        seenFiles.set(item.name, true);
        deduplicatedItems.push(item);
      }
    }
    
    return deduplicatedItems;
  };

  const renderCategoryCard = (category) => {
    const stats = getCategoryStats(category.value);
    const isSelected = selectedCategory === category.value;
    
    return (
      <Card 
        key={category.value} 
        withBorder 
        radius="lg" 
        p="xl" 
        style={{ 
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          borderColor: isSelected ? `var(--mantine-color-${category.color}-4)` : undefined,
          backgroundColor: isSelected ? `var(--mantine-color-${category.color}-0)` : undefined,
          transform: isSelected ? 'translateY(-2px)' : undefined,
          boxShadow: isSelected ? '0 8px 25px rgba(0,0,0,0.12)' : undefined
        }}
        onClick={() => setSelectedCategory(isSelected ? null : category.value)}
      >
        <Group justify="space-between" align="flex-start">
          <div style={{ flex: 1 }}>
            <Group gap="md" mb="sm">
              <Text size="2xl">{category.icon}</Text>
              <div>
                <Title order={4} style={{ marginBottom: 4 }}>{category.label}</Title>
                <Text c="dimmed" size="sm">{category.description}</Text>
              </div>
            </Group>
            
            {stats.totalFiles > 0 ? (
              <Stack gap="xs" mt="md">
                <Group gap="lg">
                  <Badge variant="light" color={category.color} size="sm">
                    {stats.totalFiles} files
                  </Badge>
                  {stats.completed > 0 && (
                    <Badge variant="light" color="green" size="sm">
                      {stats.completed} completed
                    </Badge>
                  )}
                  {stats.processing > 0 && (
                    <Badge variant="light" color="orange" size="sm">
                      {stats.processing} processing
                    </Badge>
                  )}
                </Group>
                
                {stats.totalChunks > 0 && (
                  <Text size="sm" c="dimmed">
                    {stats.totalChunks.toLocaleString()} chunks embedded
                  </Text>
                )}
              </Stack>
            ) : (
              <Text size="sm" c="dimmed" mt="md">No files uploaded</Text>
            )}
          </div>
          
          <Group gap="xs">
            {stats.totalFiles > 0 && (
              <ThemeIcon variant="light" color={category.color} size="sm">
                <Text size="xs" fw={600}>{stats.totalFiles}</Text>
              </ThemeIcon>
            )}
            <ActionIcon variant="subtle" color="gray" size="sm">
              <IconChevronRight 
                size={16} 
                style={{ 
                  transform: isSelected ? 'rotate(90deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease'
                }}
              />
            </ActionIcon>
          </Group>
        </Group>
      </Card>
    );
  };

  const renderSlidingPanel = () => {
    if (!selectedCategory) return null;
    
    const category = CATEGORIES.find(cat => cat.value === selectedCategory);
    const categoryFiles = getCategoryFiles(selectedCategory);
    const stats = getCategoryStats(selectedCategory);
    
    return (
      <Transition mounted={!!selectedCategory} transition="fade-left" duration={300} timingFunction="ease">
        {(styles) => (
          <Box style={{...styles, height: '100vh', overflow: 'auto'}}>
            <Card 
              radius="lg" 
              p="xl" 
              style={{ 
                minHeight: '100vh',
                borderLeft: `4px solid var(--mantine-color-${category.color}-4)`,
                borderRadius: '16px 0 0 16px'
              }}
            >
              {/* Panel Header */}
              <Group justify="space-between" mb="xl">
                <Group gap="md">
                  <ActionIcon 
                    variant="subtle" 
                    size="lg"
                    onClick={() => setSelectedCategory(null)}
                  >
                    <IconArrowLeft size={20} />
                  </ActionIcon>
                  <div>
                    <Group gap="sm" mb="xs">
                      <Text size="xl">{category.icon}</Text>
                      <Title order={3}>{category.label}</Title>
                    </Group>
                    <Text c="dimmed" size="sm">{category.description}</Text>
                  </div>
                </Group>
                
                <CloseButton 
                  size="lg"
                  onClick={() => setSelectedCategory(null)}
                />
              </Group>

              <Divider mb="xl" />

              {/* Stats Summary */}
              {stats.totalFiles > 0 && (
                <Group gap="lg" mb="xl">
                  <div>
                    <Text size="xl" fw={700} c={category.color}>
                      {stats.totalFiles}
                    </Text>
                    <Text size="sm" c="dimmed">Total Files</Text>
                  </div>
                  <div>
                    <Text size="xl" fw={700} c="green">
                      {stats.completed}
                    </Text>
                    <Text size="sm" c="dimmed">Completed</Text>
                  </div>
                  <div>
                    <Text size="xl" fw={700} c="blue">
                      {stats.totalChunks.toLocaleString()}
                    </Text>
                    <Text size="sm" c="dimmed">Total Chunks</Text>
                  </div>
                </Group>
              )}

              {/* Drop Zone */}
              <Box mb="xl">
                <Dropzone
                  onDrop={(files) => addFilesToList(files, selectedCategory)}
                  onReject={(files) => {
                    files.forEach(file => {
                      notifications.show({
                        title: 'File Rejected',
                        message: file.errors?.[0]?.message || 'Invalid file',
                        color: 'red'
                      });
                    });
                  }}
                  maxSize={FILE_SIZE_LIMIT}
                  accept={[
                    'application/pdf',
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                    'application/msword',
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                    'application/vnd.ms-excel',
                    'text/csv',
                    'text/plain',
                    'text/markdown',
                    'text/html',
                    'application/rtf',
                    'application/vnd.openxmlformats-officedocument.presentationml.presentation'
                  ]}
                >
                  <Group justify="center" gap="xl" style={{ minHeight: 100, pointerEvents: 'none' }}>
                    <Dropzone.Accept>
                      <IconUpload size={50} color="green" />
                    </Dropzone.Accept>
                    <Dropzone.Reject>
                      <IconX size={50} color="red" />
                    </Dropzone.Reject>
                    <Dropzone.Idle>
                      <Text size="3xl">{category.icon}</Text>
                    </Dropzone.Idle>

                    <div style={{ textAlign: 'center' }}>
                      <Text size="lg" fw={600}>Drop {category.label.toLowerCase()} here or click to select</Text>
                      <Text size="sm" c="dimmed" mt={4}>
                        Supported: {SUPPORTED_FILE_TYPES.join(', ')}
                      </Text>
                      <Text size="xs" c="dimmed">
                        Maximum 50MB per file
                      </Text>
                    </div>
                  </Group>
                </Dropzone>
              </Box>

              {/* File List */}
              {categoryFiles.length > 0 && (
                <Box>
                  <Group justify="space-between" mb="md">
                    <Title order={4}>Files ({categoryFiles.length})</Title>
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => clearCompleted(selectedCategory)}
                      disabled={!uploadedFiles.filter(item => 
                        item.category === selectedCategory && item.status === 'completed'
                      ).length}
                      leftSection={<IconTrash size={14} />}
                    >
                      Clear Completed
                    </Button>
                  </Group>

                  <Stack gap="sm">
                    {categoryFiles.map((item) => (
                      <Card key={item.id} withBorder p="md" radius="md">
                        <Group justify="space-between">
                          <Group gap="md">
                            <ThemeIcon 
                              color={getStatusColor(item.status)} 
                              variant="light" 
                              size="lg"
                            >
                              {getStatusIcon(item.status)}
                            </ThemeIcon>
                            <div style={{ flex: 1 }}>
                              <Group gap="xs" mb="xs">
                                <Text size="sm" fw={600}>
                                  {item.name}
                                </Text>
                                {item.isPersistent && (
                                  <Badge variant="dot" size="xs" color="blue">Stored</Badge>
                                )}
                                <Badge 
                                  variant="light" 
                                  size="xs" 
                                  color={getStatusColor(item.status)}
                                >
                                  {item.status}
                                </Badge>
                              </Group>
                              
                              <Group gap="md">
                                <Text size="xs" c="dimmed">
                                  {item.type} • {item.size > 0 ? (item.size / 1024 / 1024).toFixed(1) + 'MB' : 'Unknown size'}
                                </Text>
                                {item.chunksProcessed > 0 && (
                                  <Text size="xs" c="dimmed">
                                    {item.chunksProcessed}/{item.totalChunks || '?'} chunks
                                  </Text>
                                )}
                                {item.processing_time_ms && (
                                  <Text size="xs" c="dimmed">
                                    {Math.round(item.processing_time_ms / 1000)}s
                                  </Text>
                                )}
                                {item.created_at && (
                                  <Text size="xs" c="dimmed">
                                    {new Date(item.created_at).toLocaleDateString()}
                                  </Text>
                                )}
                              </Group>
                              
                              {item.error && (
                                <Text size="xs" c="red" mt="xs">{item.error}</Text>
                              )}
                              
                              {(item.status === 'uploading' || item.status === 'processing') && (
                                <Progress value={item.progress} size="sm" mt="xs" />
                              )}
                            </div>
                          </Group>
                          
                          <Group gap="xs">
                            {item.status === 'uploaded' && !item.isPersistent && (
                              <Button
                                size="xs"
                                variant="default"
                                onClick={() => processFile(item)}
                                leftSection={<IconUpload size={12} />}
                              >
                                Process
                              </Button>
                            )}
                            {item.status === 'processing' && item.isPersistent && (
                              <Button
                                size="xs"
                                variant="default"
                                onClick={fetchEmbeddingJobs}
                                leftSection={<IconRefresh size={12} />}
                              >
                                Refresh
                              </Button>
                            )}
                            {item.status === 'failed' && (
                              <Tooltip label="Retry Upload">
                                <ActionIcon 
                                  color="blue" 
                                  variant="light" 
                                  size="sm"
                                  onClick={() => retryFile(item.id)}
                                >
                                  <IconRefresh size={14} />
                                </ActionIcon>
                              </Tooltip>
                            )}
                            <Tooltip label={item.isPersistent ? "Delete File & Embeddings" : "Remove from List"}>
                              <ActionIcon 
                                color="red" 
                                variant="light" 
                                size="sm"
                                onClick={() => item.isPersistent 
                                  ? setDeleteModal({ open: true, file: item })
                                  : removeFile(item.id)
                                }
                              >
                                <IconTrash size={14} />
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        </Group>
                      </Card>
                    ))}
                  </Stack>
                </Box>
              )}
            </Card>
          </Box>
        )}
      </Transition>
    );
  };

  return (
    <>
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
      
      <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
        {/* Left Panel - Categories */}
        <Box 
          style={{ 
            width: selectedCategory ? '40%' : '100%',
            transition: 'width 0.3s ease',
            padding: '2rem',
            overflow: 'auto'
          }}
        >
          <Stack gap="xl">
            {/* Header */}
            <Box>
              <Title order={2} mb="sm">{headerTitle}</Title>
              <Text c="dimmed" size="lg">{headerSubtitle}</Text>
            </Box>

            {globalError && (
              <Alert color="red" title="Error" icon={<IconAlertCircle size={16} />}>
                {globalError}
              </Alert>
            )}

            {/* Category Cards */}
            {loading ? (
              <Stack gap="lg">
                {visibleCategories.map((category) => (
                  <Card key={category.value} withBorder radius="lg" p="xl" style={{ minHeight: '150px' }}>
                    <Group justify="center" align="center" style={{ height: '100px' }}>
                      <Stack align="center" gap="sm">
                        <IconRefresh size={32} color="gray" style={{ animation: 'spin 1s linear infinite' }} />
                        <Text size="sm" c="dimmed">Loading {category.label.toLowerCase()}...</Text>
                      </Stack>
                    </Group>
                  </Card>
                ))}
              </Stack>
            ) : (
              <Stack gap="lg">
                {visibleCategories.map((category) => renderCategoryCard(category))}
              </Stack>
            )}
          </Stack>
        </Box>

        {/* Right Panel - Sliding File Management */}
        {selectedCategory && (
          <Box style={{ width: '60%', borderLeft: '1px solid var(--mantine-color-gray-2)' }}>
            {renderSlidingPanel()}
          </Box>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, file: null })}
        title={
          <Group gap="sm">
            <IconTrash size={20} color="red" />
            <Text fw={600}>Delete File</Text>
          </Group>
        }
        centered
      >
        <Stack gap="md">
          <Text>
            Are you sure you want to delete <Text span fw={600}>{deleteModal.file?.name}</Text>?
          </Text>
          
          {deleteModal.file?.status === 'completed' && (
            <Alert color="orange" title="Important" variant="light">
              <Text size="sm">
                This will permanently delete the file and all its embeddings. 
                Previous risk assessments using this data remain unchanged, 
                but future analyses may produce different results.
              </Text>
            </Alert>
          )}

          {deleteModal.file?.status !== 'completed' && (
            <Alert color="blue" title="Note" variant="light">
              <Text size="sm">
                This will delete the uploaded file from storage. 
                No embeddings will be affected as the file hasn't been processed yet.
              </Text>
            </Alert>
          )}

          <Group justify="flex-end" gap="sm">
            <Button
              variant="default"
              onClick={() => setDeleteModal({ open: false, file: null })}
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={() => handleDeleteFile(deleteModal.file)}
              leftSection={<IconTrash size={16} />}
            >
              Delete {deleteModal.file?.status === 'completed' ? 'File & Embeddings' : 'File'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}

export default UniversalFileUploader;// import React, { useState, useCallback, useEffect, useMemo } from 'react';
// import {
//   Container,
//   Title,
//   Text,
//   Card,
//   Group,
//   Button,
//   Stack,
//   Alert,
//   Progress,
//   Badge,
//   Grid,
//   Box,
//   ActionIcon,
//   Tooltip,
//   ThemeIcon,
//   Modal
// } from '@mantine/core';
// import { Dropzone } from '@mantine/dropzone';
// import { notifications } from '@mantine/notifications';
// import {
//   IconUpload,
//   IconFile,
//   IconX,
//   IconCheck,
//   IconAlertCircle,
//   IconRefresh,
//   IconTrash,
// } from '@tabler/icons-react';
// import { useAuth0 } from '@auth0/auth0-react';

// const CATEGORIES = [
//   { 
//     value: 'risk-catalogues', 
//     label: 'Risk Catalogues', 
//     description: 'NIST, ISO, industry frameworks',
//     icon: '📊',
//     color: 'blue'
//   },
//   { 
//     value: 'policy-governance', 
//     label: 'Governance Policies', 
//     description: 'Corporate governance, board policies',
//     icon: '🏛️',
//     color: 'violet'
//   },
//   { 
//     value: 'policy-security', 
//     label: 'Security Policies', 
//     description: 'Information security, data protection',
//     icon: '🔒',
//     color: 'red'
//   },
//   { 
//     value: 'policy-ai-control', 
//     label: 'AI Control Policies', 
//     description: 'AI governance, model management',
//     icon: '🤖',
//     color: 'green'
//   }
// ];

// const SUPPORTED_FILE_TYPES = [
//   'PDF', 'DOCX', 'DOC', 'XLSX', 'XLS', 'CSV', 'TXT', 'MD', 'HTML', 'RTF', 'PPTX'
// ];

// const FILE_SIZE_LIMIT = 50 * 1024 * 1024; // 50MB

// function UniversalFileUploader() {
//   const { getAccessTokenSilently } = useAuth0();
//   const [uploadedFiles, setUploadedFiles] = useState([]);
//   const [embeddingJobs, setEmbeddingJobs] = useState([]);
//   const [expandedCards, setExpandedCards] = useState({});
//   const [globalError] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [deleteModal, setDeleteModal] = useState({ open: false, file: null });

//   // Fetch embedding jobs from Supabase for UI display (observability only)
//   const fetchEmbeddingJobs = useCallback(async () => {
//     try {
//       const token = await getAccessTokenSilently({
//         authorizationParams: {
//           audience: process.env.REACT_APP_AUTH0_AUDIENCE || 'auth.agenthuginn.ai',
//           scope: 'read:models write:models'
//         }
//       });

//       const response = await fetch('/.netlify/functions/getEmbeddingJobs', {
//         method: 'GET',
//         headers: {
//           'Authorization': `Bearer ${token}`,
//           'Content-Type': 'application/json'
//         }
//       });

//       if (response.ok) {
//         const jobs = await response.json();
//         setEmbeddingJobs(jobs);
//       }
//     } catch (error) {
//       console.error('Failed to fetch embedding jobs:', error);
//     } finally {
//       setLoading(false);
//     }
//   }, [getAccessTokenSilently]);

//   // Load data on mount
//   useEffect(() => {
//     fetchEmbeddingJobs();
//   }, [fetchEmbeddingJobs]);

//   // File status: 'uploading', 'uploaded', 'processing', 'completed', 'failed'
//   const uploadFileToSupabase = useCallback(async (fileItem) => {
//     try {
//       console.log(`[UniversalFileUploader] Starting upload for file: ${fileItem.name}`);
//       const token = await getAccessTokenSilently({
//         authorizationParams: {
//           audience: process.env.REACT_APP_AUTH0_AUDIENCE || 'auth.agenthuginn.ai',
//           scope: 'read:models write:models'
//         }
//       });

//       const bucket = getDynamicBucket(fileItem.category);
//       const path = `${fileItem.category}/${fileItem.name}`;
//       const contentType = fileItem.file.type || 'application/octet-stream';
      
//       console.log(`[UniversalFileUploader] Uploading to bucket: ${bucket}, path: ${path}`);

//       const uploadResponse = await fetch('/.netlify/functions/supaCreateSignedUpload', {
//         method: 'POST',
//         headers: {
//           'Authorization': `Bearer ${token}`,
//           'Content-Type': 'application/json'
//         },
//         body: JSON.stringify({
//           bucket,
//           path,
//           contentType
//         })
//       });

//       if (!uploadResponse.ok) {
//         throw new Error(`Upload failed: ${uploadResponse.statusText}`);
//       }

//       const uploadData = await uploadResponse.json();

//       // Upload file to signed URL  
//       const fileUploadResponse = await fetch(uploadData.signedURL || uploadData.signedUrl, {
//         method: 'PUT',
//         body: fileItem.file,
//         headers: {
//           'Content-Type': contentType
//         }
//       });

//       if (!fileUploadResponse.ok) {
//         throw new Error('Failed to upload file to storage');
//       }

//       console.log(`[UniversalFileUploader] File uploaded successfully: ${fileItem.name}`);
      
//       // Update file status to uploaded
//       setUploadedFiles(prev => prev.map(item => 
//         item.id === fileItem.id ? { 
//           ...item, 
//           status: 'uploaded', 
//           progress: 100,
//           supabasePath: uploadData.path || path
//         } : item
//       ));

//       notifications.show({
//         title: 'File Uploaded',
//         message: `${fileItem.name} uploaded successfully. Click "Process" to embed.`,
//         color: 'green',
//         icon: <IconCheck size={16} />
//       });

//     } catch (error) {
//       console.error('File upload error:', error);
      
//       setUploadedFiles(prev => prev.map(item => 
//         item.id === fileItem.id ? { 
//           ...item, 
//           status: 'failed', 
//           progress: 0,
//           error: error.message 
//         } : item
//       ));

//       notifications.show({
//         title: 'Upload Failed',
//         message: `${fileItem.name}: ${error.message}`,
//         color: 'red',
//         icon: <IconX size={16} />
//       });
//     }
//   }, [getAccessTokenSilently]);

//   const addFilesToList = useCallback((files, category) => {
//     files.forEach(file => {
//       // Check for duplicates in both session files and persistent jobs
//       const existingInSession = uploadedFiles.some(item => 
//         item.name === file.name && item.category === category
//       );
//       const existingInJobs = embeddingJobs.some(job => 
//         job.file_name === file.name && job.category === category && job.status === 'completed'
//       );
      
//       if (existingInSession || existingInJobs) {
//         notifications.show({
//           title: 'Duplicate File',
//           message: `${file.name} has already been uploaded${existingInJobs ? ' and processed' : ''}`,
//           color: 'orange',
//           icon: <IconAlertCircle size={16} />
//         });
//         return;
//       }

//       const fileItem = {
//         id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
//         file,
//         name: file.name,
//         size: file.size,
//         type: file.name.split('.').pop().toUpperCase(),
//         status: 'uploading',
//         progress: 0,
//         error: null,
//         supabasePath: null,
//         category
//       };
      
//       setUploadedFiles(prev => [...prev, fileItem]);
//       uploadFileToSupabase(fileItem);
//     });
//   }, [uploadFileToSupabase, uploadedFiles, embeddingJobs]);

//   const getDynamicBucket = (category) => {
//     if (category === 'risk-catalogues') return 'risk-catalogues';
//     if (category.startsWith('policy-')) return 'policies';
//     return 'documents'; // fallback
//   };

//   const processFile = async (fileItem) => {
//     try {
//       console.log(`[UniversalFileUploader] Processing file: ${fileItem.name}`);
//       const token = await getAccessTokenSilently({
//         authorizationParams: {
//           audience: process.env.REACT_APP_AUTH0_AUDIENCE || 'auth.agenthuginn.ai',
//           scope: 'read:models write:models'
//         }
//       });

//       // Update status to processing
//       setUploadedFiles(prev => prev.map(item => 
//         item.id === fileItem.id ? { ...item, status: 'processing', progress: 25 } : item
//       ));

//       // Use EXACT same approach as model cards: supaLlamaStart + supaLlamaFinalize
//       const bucket = getDynamicBucket(fileItem.category);
//       const path = fileItem.supabasePath;

//       // Step 1: Start LlamaParse (returns jobId immediately like model cards)
//       const startResponse = await fetch('/.netlify/functions/supaLlamaStart', {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json',
//           'Authorization': `Bearer ${token}`
//         },
//         body: JSON.stringify({
//           bucket,
//           path,
//           fileName: fileItem.name
//         })
//       });

//       if (!startResponse.ok) {
//         const errorData = await startResponse.json().catch(() => ({}));
//         throw new Error(errorData.error || `Start failed: ${startResponse.statusText}`);
//       }

//       const startData = await startResponse.json();
//       const jobId = startData.jobId;
//       console.log(`[UniversalFileUploader] LlamaParse started, jobId: ${jobId}`);

//       // Update status to processing
//       setUploadedFiles(prev => prev.map(item => 
//         item.id === fileItem.id ? { 
//           ...item, 
//           status: 'processing', 
//           progress: 25,
//           jobId
//         } : item
//       ));

//       // Step 2: Poll with supaLlamaFinalize (exact model card approach)
//       pollLlamaJob(fileItem, jobId, bucket, path);

//     } catch (error) {
//       console.error('File processing error:', error);
      
//       setUploadedFiles(prev => prev.map(item => 
//         item.id === fileItem.id ? { 
//           ...item, 
//           status: 'failed', 
//           progress: 0,
//           error: error.message 
//         } : item
//       ));

//       notifications.show({
//         title: 'Processing Failed',
//         message: `${fileItem.name}: ${error.message}`,
//         color: 'red',
//         icon: <IconX size={16} />
//       });
//     }
//   };

//   const pollLlamaJob = async (fileItem, jobId, bucket, path) => {
//     // EXACT same polling logic as model cards in AutopilotAssessmentFlow.jsx
//     let attempts = 0;
//     const maxAttempts = 30;

//     const poll = async () => {
//       try {
//         const token = await getAccessTokenSilently({
//           authorizationParams: {
//             audience: process.env.REACT_APP_AUTH0_AUDIENCE || 'auth.agenthuginn.ai',
//             scope: 'read:models write:models'
//           }
//         });

//         const finResponse = await fetch('/.netlify/functions/supaLlamaFinalize', {
//           method: 'POST',
//           headers: {
//             'Content-Type': 'application/json',
//             'Authorization': `Bearer ${token}`
//           },
//           body: JSON.stringify({
//             jobId,
//             bucket,
//             path,
//             fileName: fileItem.name,
//             category: fileItem.category, // Add category for proper table routing
//             saveMarkdown: true
//           })
//         });

//         // Handle 202 status (still processing) - this is normal!
//         if (finResponse.status === 202) {
//           // Still processing, continue polling (same as model cards)
//           attempts++;
//           if (attempts < maxAttempts) {
//             const statusData = await finResponse.json().catch(() => ({}));
//             console.log(`[UniversalFileUploader] Polling attempt ${attempts}/${maxAttempts} for ${fileItem.name}, status: ${statusData.status}`);
//             setUploadedFiles(prev => prev.map(item => 
//               item.id === fileItem.id ? { 
//                 ...item, 
//                 progress: Math.min(25 + (attempts * 2), 90) // Gradual progress
//               } : item
//             ));
//             setTimeout(poll, 3000); // Same 3s interval as model cards
//             return;
//           } else {
//             throw new Error('Polling timeout after 30 attempts');
//           }
//         }

//         // Handle other error statuses  
//         if (!finResponse.ok) {
//           const errorText = await finResponse.text();
//           throw new Error(`Finalize failed: ${errorText}`);
//         }

//         // Success! Parse response
//         const finData = await finResponse.json();
//         console.log(`[UniversalFileUploader] Processing completed for ${fileItem.name}:`, finData);

//         // Update to completed
//         setUploadedFiles(prev => prev.map(item => 
//           item.id === fileItem.id ? { 
//             ...item, 
//             status: 'completed',
//             progress: 100,
//             chunksProcessed: finData.embeddedChunks,
//             totalChunks: finData.totalChunks
//           } : item
//         ));

//         notifications.show({
//           title: 'Processing Complete',
//           message: `${fileItem.name} processed successfully (${finData.embeddedChunks}/${finData.totalChunks} chunks)`,
//           color: 'green',
//           icon: <IconCheck size={16} />
//         });

//       } catch (error) {
//         console.error(`Polling error for ${fileItem.name}:`, error);
        
//         setUploadedFiles(prev => prev.map(item => 
//           item.id === fileItem.id ? { 
//             ...item, 
//             status: 'failed',
//             error: error.message
//           } : item
//         ));

//         notifications.show({
//           title: 'Processing Failed',
//           message: `${fileItem.name}: ${error.message}`,
//           color: 'red',
//           icon: <IconX size={16} />
//         });
//       }
//     };

//     // Start polling immediately
//     poll();
//   };

//   const removeFile = (fileId) => {
//     setUploadedFiles(prev => prev.filter(item => item.id !== fileId));
//   };

//   const retryFile = async (fileId) => {
//     const fileItem = uploadedFiles.find(item => item.id === fileId);
//     if (fileItem) {
//       // Reset status and retry upload
//       setUploadedFiles(prev => prev.map(item => 
//         item.id === fileId ? { ...item, status: 'uploading', progress: 0, error: null } : item
//       ));
      
//       // Retry the actual upload
//       uploadFileToSupabase(fileItem);
//     }
//   };

//   const clearCompleted = (category = null) => {
//     if (category) {
//       setUploadedFiles(prev => prev.filter(item => 
//         !(item.category === category && item.status === 'completed')
//       ));
//     } else {
//       setUploadedFiles(prev => prev.filter(item => item.status !== 'completed'));
//     }
//   };

//   const handleDeleteFile = useCallback(async (fileToDelete) => {
//     try {
//       const token = await getAccessTokenSilently({
//         authorizationParams: {
//           audience: process.env.REACT_APP_AUTH0_AUDIENCE || 'auth.agenthuginn.ai',
//           scope: 'read:models write:models'
//         }
//       });

//       const response = await fetch('/.netlify/functions/deleteFile', {
//         method: 'POST',
//         headers: {
//           'Authorization': `Bearer ${token}`,
//           'Content-Type': 'application/json'
//         },
//         body: JSON.stringify({
//           fileName: fileToDelete.name,
//           category: fileToDelete.category,
//           jobId: fileToDelete.isPersistent ? fileToDelete.id : null,
//           deleteEmbeddings: fileToDelete.status === 'completed'
//         })
//       });

//       if (!response.ok) {
//         throw new Error('Failed to delete file');
//       }

//       // Remove from local state if it's a session file
//       if (!fileToDelete.isPersistent) {
//         setUploadedFiles(prev => prev.filter(item => item.id !== fileToDelete.id));
//       }

//       // Refresh embedding jobs to update persistent files
//       await fetchEmbeddingJobs();

//       notifications.show({
//         title: 'File Deleted',
//         message: `${fileToDelete.name} has been deleted successfully`,
//         color: 'green',
//         icon: <IconCheck size={16} />
//       });

//       setDeleteModal({ open: false, file: null });

//     } catch (error) {
//       console.error('Delete file error:', error);
//       notifications.show({
//         title: 'Delete Failed',
//         message: error.message,
//         color: 'red',
//         icon: <IconX size={16} />
//       });
//     }
//   }, [getAccessTokenSilently, fetchEmbeddingJobs]);

//   const toggleCardExpansion = (category) => {
//     setExpandedCards(prev => ({
//       ...prev,
//       [category]: !prev[category]
//     }));
//   };

//   const getCategoryStats = (category) => {
//     // Combine current session files with persistent embedding jobs
//     const categoryFiles = uploadedFiles.filter(item => item.category === category);
//     const categoryJobs = embeddingJobs.filter(job => job.category === category);
    
//     // Stats from current session
//     const sessionCompleted = categoryFiles.filter(item => item.status === 'completed').length;
//     const sessionProcessing = categoryFiles.filter(item => item.status === 'processing').length;
//     const sessionFailed = categoryFiles.filter(item => item.status === 'failed').length;
//     const sessionChunks = categoryFiles.reduce((sum, item) => sum + (item.chunksProcessed || 0), 0);
    
//     // Stats from persistent jobs
//     const jobsCompleted = categoryJobs.filter(job => job.status === 'completed').length;
//     const jobsProcessing = categoryJobs.filter(job => job.status === 'processing').length;
//     const jobsFailed = categoryJobs.filter(job => job.status === 'failed').length;
//     const jobsChunks = categoryJobs.reduce((sum, job) => sum + (job.chunks_processed || 0), 0);
    
//     return {
//       totalFiles: categoryFiles.length + categoryJobs.length,
//       completed: sessionCompleted + jobsCompleted,
//       processing: sessionProcessing + jobsProcessing,
//       failed: sessionFailed + jobsFailed,
//       totalChunks: sessionChunks + jobsChunks
//     };
//   };

//   const getStatusColor = (status) => {
//     const colors = {
//       uploading: 'blue',
//       uploaded: 'cyan',
//       processing: 'yellow',
//       completed: 'green',
//       failed: 'red'
//     };
//     return colors[status] || 'gray';
//   };

//   const getStatusIcon = (status) => {
//     const icons = {
//       uploading: <IconUpload size={16} />,
//       uploaded: <IconCheck size={16} />,
//       processing: <IconRefresh size={16} />,
//       completed: <IconCheck size={16} />,
//       failed: <IconX size={16} />
//     };
//     return icons[status] || <IconFile size={16} />;
//   };

//   const renderCategoryCard = (category) => {
//     const stats = getCategoryStats(category.value);
//     const isExpanded = expandedCards[category.value];
//     const categoryFiles = uploadedFiles.filter(item => item.category === category.value);
//     const categoryJobs = embeddingJobs.filter(job => job.category === category.value);
    
//     // Combine session files and persistent jobs for display
//     const allCategoryItems = [
//       ...categoryFiles,
//       ...categoryJobs.map(job => ({
//         id: job.job_id,
//         name: job.file_name,
//         type: job.file_type,
//         size: job.file_size || 0,
//         status: job.status,
//         chunksProcessed: job.chunks_processed || 0,
//         totalChunks: job.total_chunks || 0,
//         error: job.error_message,
//         category: job.category,
//         isPersistent: true,
//         created_at: job.created_at,
//         processing_time_ms: job.processing_time_ms
//       }))
//     ];
    
//     // Deduplicate by filename, keeping the most recent/complete entry
//     const deduplicatedItems = [];
//     const seenFiles = new Map();
    
//     // Sort by creation time (newest first) before deduplication
//     allCategoryItems.sort((a, b) => {
//       const aTime = a.created_at || a.uploadedAt || 0;
//       const bTime = b.created_at || b.uploadedAt || 0;
//       return new Date(bTime) - new Date(aTime);
//     });
    
//     // Deduplicate: keep first occurrence (newest) of each filename
//     for (const item of allCategoryItems) {
//       if (!seenFiles.has(item.name)) {
//         seenFiles.set(item.name, true);
//         deduplicatedItems.push(item);
//       }
//     }
    
//     const finalItems = deduplicatedItems;

//     return (
//       <Box key={category.value} mb="xl">
//         {/* Category Card */}
//         <Card 
//           withBorder 
//           radius="md" 
//           p="xl" 
//           style={{ cursor: 'pointer', minHeight: '200px' }}
//           onClick={() => toggleCardExpansion(category.value)}
//         >
//           <Group justify="space-between" align="flex-start">
//             <div style={{ flex: 1 }}>
//               <Group gap="sm" mb="xs">
//                 <Text size="xl">{category.icon}</Text>
//                 <Title order={3}>{category.label}</Title>
//               </Group>
//               <Text c="dimmed" size="sm" mb="md">{category.description}</Text>
              
//               {stats.totalFiles > 0 ? (
//                 <Stack gap="xs">
//                   <Text size="sm">
//                     • {stats.totalFiles} files uploaded
//                   </Text>
//                   <Text size="sm">
//                     • {stats.completed} completed, {stats.processing} processing
//                     {stats.failed > 0 && `, ${stats.failed} failed`}
//                   </Text>
//                   <Text size="sm">
//                     • {stats.totalChunks.toLocaleString()} total chunks
//                   </Text>
//                 </Stack>
//               ) : (
//                 <Text size="sm" c="dimmed">No files uploaded</Text>
//               )}
//             </div>
            
//             <Badge 
//               variant="light"
//               rightSection={isExpanded ? '▲' : '▼'}
//             >
//               {isExpanded ? 'Collapse' : 'Expand'}
//             </Badge>
//           </Group>
//         </Card>

//         {/* Expanded Content */}
//         {isExpanded && (
//           <Card withBorder radius="md" p="lg" mt="md" style={{ borderTop: 'none' }}>
//             {/* Drop Zone */}
//             <Box mb="xl">
//               <Dropzone
//                 onDrop={(files) => addFilesToList(files, category.value)}
//                 onReject={(files) => {
//                   files.forEach(file => {
//                     notifications.show({
//                       title: 'File Rejected',
//                       message: file.errors?.[0]?.message || 'Invalid file',
//                       color: 'red'
//                     });
//                   });
//                 }}
//                 maxSize={FILE_SIZE_LIMIT}
//                 accept={[
//                   'application/pdf',
//                   'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
//                   'application/msword',
//                   'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
//                   'application/vnd.ms-excel',
//                   'text/csv',
//                   'text/plain',
//                   'text/markdown',
//                   'text/html',
//                   'application/rtf',
//                   'application/vnd.openxmlformats-officedocument.presentationml.presentation'
//                 ]}
//               >
//                 <Group justify="center" gap="xl" style={{ minHeight: 80, pointerEvents: 'none' }}>
//                   <Dropzone.Accept>
//                     <IconUpload size={40} color="green" />
//                   </Dropzone.Accept>
//                   <Dropzone.Reject>
//                     <IconX size={40} color="red" />
//                   </Dropzone.Reject>
//                   <Dropzone.Idle>
//                     <Text size="lg">{category.icon}</Text>
//                   </Dropzone.Idle>

//                   <div>
//                     <Text size="lg" inline>Drop {category.label.toLowerCase()} here or click to select</Text>
//                     <Text size="sm" c="dimmed" inline mt={4}>
//                       Supported: {SUPPORTED_FILE_TYPES.join(', ')} (max 50MB each)
//                     </Text>
//                   </div>
//                 </Group>
//               </Dropzone>
//             </Box>

//             {/* File Table */}
//             {finalItems.length > 0 && (
//               <Box>
//                 <Group justify="space-between" mb="md">
//                   <Title order={4}>Files ({finalItems.length})</Title>
//                   <Button
//                     variant="default"
//                     size="sm"
//                     onClick={() => clearCompleted(category.value)}
//                     disabled={!categoryFiles.some(item => item.status === 'completed')}
//                     leftSection={<IconTrash size={14} />}
//                   >
//                     Clear Completed
//                   </Button>
//                 </Group>

//                 <Stack gap="xs">
//                   {finalItems.map((item) => (
//                     <Card key={item.id} withBorder p="sm" style={{ backgroundColor: item.status === 'failed' ? '#fff5f5' : undefined }}>
//                       <Group justify="space-between">
//                         <Group gap="sm">
//                           <ThemeIcon color={getStatusColor(item.status)} variant="light" size="sm">
//                             {getStatusIcon(item.status)}
//                           </ThemeIcon>
//                           <div>
//                             <Text size="sm" fw={500}>
//                               {item.name}
//                               {item.isPersistent && <Badge variant="dot" size="xs" ml="xs">Stored</Badge>}
//                             </Text>
//                             <Text size="xs" c="dimmed">
//                               {item.type} • {item.size > 0 ? (item.size / 1024 / 1024).toFixed(1) + 'MB' : 'Unknown size'}
//                               {item.chunksProcessed > 0 && ` • ${item.chunksProcessed}/${item.totalChunks || '?'} chunks`}
//                               {item.processing_time_ms && ` • ${Math.round(item.processing_time_ms / 1000)}s`}
//                               {item.created_at && ` • ${new Date(item.created_at).toLocaleDateString()}`}
//                             </Text>
//                             {item.error && (
//                               <Text size="xs" c="red">{item.error}</Text>
//                             )}
//                           </div>
//                         </Group>
//                         <Group gap="xs">
//                           <Badge color={getStatusColor(item.status)} variant="light" size="sm">
//                             {item.status}
//                           </Badge>
//                           {item.status === 'uploaded' && !item.isPersistent && (
//                             <Button
//                               size="xs"
//                               onClick={() => processFile(item)}
//                               leftSection={<IconUpload size={12} />}
//                             >
//                               Process
//                             </Button>
//                           )}
//                           {item.status === 'processing' && item.isPersistent && (
//                             <Button
//                               size="xs"
//                               variant="default"
//                               onClick={fetchEmbeddingJobs}
//                               leftSection={<IconRefresh size={12} />}
//                             >
//                               Refresh
//                             </Button>
//                           )}
//                           {item.status === 'failed' && (
//                             <Tooltip label="Retry Upload">
//                               <ActionIcon 
//                                 color="blue" 
//                                 variant="light" 
//                                 size="sm"
//                                 onClick={() => retryFile(item.id)}
//                               >
//                                 <IconRefresh size={14} />
//                               </ActionIcon>
//                             </Tooltip>
//                           )}
//                           <Tooltip label={item.isPersistent ? "Delete File & Embeddings" : "Remove from List"}>
//                             <ActionIcon 
//                               color="red" 
//                               variant="light" 
//                               size="sm"
//                               onClick={() => item.isPersistent 
//                                 ? setDeleteModal({ open: true, file: item })
//                                 : removeFile(item.id)
//                               }
//                             >
//                               <IconTrash size={14} />
//                             </ActionIcon>
//                           </Tooltip>
//                         </Group>
//                       </Group>
//                       {(item.status === 'uploading' || item.status === 'processing') && (
//                         <Progress value={item.progress} size="xs" mt="xs" />
//                       )}
//                     </Card>
//                   ))}
//                 </Stack>

//                 {/* Category Summary */}
//                 <Text size="sm" c="dimmed" mt="md">
//                   Total: {stats.totalFiles} files, {stats.completed} completed, {stats.totalChunks.toLocaleString()} chunks embedded
//                 </Text>
//               </Box>
//             )}
//           </Card>
//         )}
//       </Box>
//     );
//   };

//   return (
//     <>
//       <style>
//         {`
//           @keyframes spin {
//             from { transform: rotate(0deg); }
//             to { transform: rotate(360deg); }
//           }
//         `}
//       </style>
//       <Container size="xl" py="xl">
//         <Stack gap="xl">
//         {/* Header */}
//         <Box>
//           <Title order={2} mb="sm">Risk Catalogues and Policies</Title>
//           <Text c="dimmed" size="lg">
//             Upload documents for AI-powered risk analysis and policy management. 
//             All files are processed using LlamaParse for consistent extraction and embedding.
//           </Text>
//         </Box>

//         {/* Category Cards Grid */}
//         {loading ? (
//           <Grid>
//             {CATEGORIES.map((category) => (
//               <Grid.Col key={category.value} span={{ base: 12, md: 6 }}>
//                 <Card withBorder radius="md" p="xl" style={{ minHeight: '200px' }}>
//                   <Group justify="center" align="center" style={{ height: '150px' }}>
//                     <Stack align="center" gap="sm">
//                       <IconRefresh size={32} color="gray" style={{ animation: 'spin 1s linear infinite' }} />
//                       <Text size="sm" c="dimmed">Loading {category.label.toLowerCase()}...</Text>
//                     </Stack>
//                   </Group>
//                 </Card>
//               </Grid.Col>
//             ))}
//           </Grid>
//         ) : (
//           <Grid>
//             {CATEGORIES.map((category, index) => (
//               <Grid.Col 
//                 key={category.value} 
//                 span={{ base: 12, md: expandedCards[category.value] ? 12 : 6 }}
//               >
//                 {renderCategoryCard(category)}
//               </Grid.Col>
//             ))}
//           </Grid>
//         )}


//         {globalError && (
//           <Alert color="red" title="Error" icon={<IconAlertCircle size={16} />}>
//             {globalError}
//           </Alert>
//         )}
//         </Stack>
//       </Container>

//       {/* Delete Confirmation Modal */}
//       <Modal
//         opened={deleteModal.open}
//         onClose={() => setDeleteModal({ open: false, file: null })}
//         title={
//           <Group gap="sm">
//             <IconTrash size={20} color="red" />
//             <Text fw={600}>Delete File</Text>
//           </Group>
//         }
//         centered
//       >
//         <Stack gap="md">
//           <Text>
//             Are you sure you want to delete <Text span fw={600}>{deleteModal.file?.name}</Text>?
//           </Text>
          
//           {deleteModal.file?.status === 'completed' && (
//             <Alert color="orange" title="Important" variant="light">
//               <Text size="sm">
//                 This will permanently delete the file and all its embeddings. 
//                 Previous risk assessments using this data remain unchanged, 
//                 but future analyses may produce different results.
//               </Text>
//             </Alert>
//           )}

//           {deleteModal.file?.status !== 'completed' && (
//             <Alert color="blue" title="Note" variant="light">
//               <Text size="sm">
//                 This will delete the uploaded file from storage. 
//                 No embeddings will be affected as the file hasn't been processed yet.
//               </Text>
//             </Alert>
//           )}

//           <Group justify="flex-end" gap="sm">
//             <Button
//               variant="default"
//               onClick={() => setDeleteModal({ open: false, file: null })}
//             >
//               Cancel
//             </Button>
//             <Button
//               color="red"
//               onClick={() => handleDeleteFile(deleteModal.file)}
//               leftSection={<IconTrash size={16} />}
//             >
//               Delete {deleteModal.file?.status === 'completed' ? 'File & Embeddings' : 'File'}
//             </Button>
//           </Group>
//         </Stack>
//       </Modal>
//     </>
//   );
// }

// export default UniversalFileUploader;